// dailyModulesCompleted.js — Modal content for Daily Modules Completed KPI

import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { DAILY_MODULES_HISTORY } from "../../../mockData/mockProductionMilestonesData";

const barColor = (pct) => {
  if (pct >= 100) return "#38a169";
  if (pct >= 60) return "#d69e2e";
  return "#cc0000";
};

const statusLabel = (pct) => {
  if (pct >= 100) return "Met/Exceeded";
  if (pct >= 60) return "Near";
  return "Missed";
};

const statusClass = (pct) => {
  if (pct >= 100) return "pml-variance-cell--pos";
  if (pct >= 60) return "";
  return "pml-variance-cell--neg";
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: barColor(d.pct) }}
        />
        {d.actual} actual / {d.projected} projected ({d.pct.toFixed(1)}%)
      </div>
    </div>
  );
};

const DailyModulesCompleted = ({ goals }) => {
  const target = goals?.dailyModulesCompleted || 90.0;

  const avgPct = (
    DAILY_MODULES_HISTORY.reduce((s, d) => s + d.pct, 0) /
    DAILY_MODULES_HISTORY.length
  ).toFixed(1);

  const metCount = DAILY_MODULES_HISTORY.filter((d) => d.pct >= 100).length;
  const nearCount = DAILY_MODULES_HISTORY.filter(
    (d) => d.pct >= 80 && d.pct < 100,
  ).length;
  const missedCount = DAILY_MODULES_HISTORY.filter((d) => d.pct < 80).length;

  const totalActual = DAILY_MODULES_HISTORY.reduce((s, d) => s + d.actual, 0);
  const totalProjected = DAILY_MODULES_HISTORY.reduce(
    (s, d) => s + d.projected,
    0,
  );

  return (
    <div className="pml-modal-flex-content">
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--4">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Month Avg %</span>
          <span
            className={`pml-modal-stat-value ${parseFloat(avgPct) >= target ? "pml-modal-stat-value--ok" : parseFloat(avgPct) >= target - 10 ? "pml-modal-stat-value--amber" : "pml-modal-stat-value--alert"}`}
          >
            {avgPct}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">
            Days Met/Exceeded {`(100%)`}
          </span>
          <span className="pml-modal-stat-value pml-modal-stat-value--ok">
            {metCount}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Days Near {`(80-99%)`}</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--amber">
            {nearCount}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Days Missed (&lt;80%)</span>
          <span
            className={`pml-modal-stat-value ${missedCount > 0 ? "pml-modal-stat-value--alert" : "pml-modal-stat-value--ok"}`}
          >
            {missedCount}
          </span>
        </div>
      </div>

      {/* Bar chart */}
      <p className="pml-modal-sub-title">Daily Completion % — This Month</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={DAILY_MODULES_HISTORY}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
              interval={2}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}%`}
              domain={[0, 120]}
              width={38}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={target}
              stroke="#1a1a1a"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              label={{
                value: `Target ${target}%`,
                position: "insideTopRight",
                fontSize: 11,
                fill: "#1a1a1a",
              }}
            />
            <Bar dataKey="pct" radius={[3, 3, 0, 0]}>
              {DAILY_MODULES_HISTORY.map((entry, i) => (
                <Cell key={i} fill={barColor(entry.pct)} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Daily log table */}
      <p className="pml-modal-sub-title">Daily Log</p>
      <div className="pml-modal-scroll-wrap pml-table-auto-height">
        <table className="pml-modal-table">
          <thead>
            <tr>
              <th>Date</th>
              <th style={{ textAlign: "center" }}>Target</th>
              <th style={{ textAlign: "center" }}>Actual</th>
              <th style={{ textAlign: "center" }}>% Complete</th>
              <th style={{ textAlign: "center" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {DAILY_MODULES_HISTORY.map((row, i) => (
              <tr key={i}>
                <td>{row.date}</td>
                <td style={{ textAlign: "center" }}>{row.projected}</td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>
                  {row.actual}
                </td>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: barColor(row.pct),
                  }}
                >
                  {row.pct.toFixed(1)}%
                </td>
                <td
                  style={{ textAlign: "center" }}
                  className={statusClass(row.pct)}
                >
                  {statusLabel(row.pct)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 12, color: "#a0aec0" }}>
        Month total: {totalActual} actual modules / {totalProjected} projected (
        {((totalActual / totalProjected) * 100).toFixed(1)}% overall)
      </div>
    </div>
  );
};

export default DailyModulesCompleted;
